import { muiColors } from "@/theme/designSystem";
import { useHref, useLocation, useNavigate, useParams } from "react-router-dom";
import ChatInput from "./chat-input";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { conversationsApi } from "@/services/conversation-service";
import { RenderConversation } from "./render-conversation";
import { cn } from "@/utils/cn";
import { PlaygroundNavbar } from "./playground-navbar";
import { ChatFormData, INewConversation } from "@/types";
import { useForm } from "react-hook-form";
import { Spinner } from "@/components/ui/spinner";
import {
  useMessagesStore,
  usePlaygroundParametersStore,
} from "@/atoms/playground";

import { useStickToBottom } from "use-stick-to-bottom";
import { useConversation } from "@/hooks/useConversation";
import { useStream } from "@/hooks/useStream";
import { createContext, useEffect, useState } from "react";
import { uploadDocument } from "@/utils/axios-v2";
import { useTheme } from "@mui/material";
import { useStoredTheme } from "@/utils/theme-context";
import NewPlaygroundLogo from "@/assets/icons/new-playground-logo";
import { Button } from "@/components/ui/button";
import { X, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";
import PlaygroundUsageToast from "./playground-usage-toast";
import { useOrganization } from "@/contexts/OrganizationContext";
import { usePermissions } from "@/hooks/usePermissions";
import { normalizeOptionalLanguage } from "@/utils/language";

export const ToolsContext = createContext<{
  tools: Array<string>;
  setTools: React.Dispatch<React.SetStateAction<string[]>>;
} | null>(null);

export const PlaygroundMain = () => {
  const { i18n, t } = useTranslation();
  const storedTheme = useStoredTheme();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [newPlaygroundInfo, setNewPlaygroundInfo] = useState(
    localStorage.getItem("playground-info-dismissed") !== "true",
  );
  const { id } = useParams();
  const [tools, setTools] = useState<Array<string>>([]);
  const stick = useStickToBottom();
  const { scrollToBottom } = stick;
  const { messages, setMessages } = useMessagesStore();
  const {
    streamMessage,
    agentSteps,
    streamActions,
    isStreaming,
    isPlanning,
    isThinking,
    reset,
    stop,
  } = useStream();
  const {
    llm_config_id,
    planner_llm_config,
    mode,
    temperature,
    max_tokens,
    language,
    setLanguage,
  } = usePlaygroundParametersStore();
  const { currentOrganization } = useOrganization();
  const { isInferenceOnly } = usePermissions();
  const uploadFilesToConversation = async (
    conversationId: string,
    files: File[],
  ) => {
    if (files.length === 0) return;

    await Promise.allSettled(
      files.map((file) => uploadDocument(conversationId, file)),
    );
  };

  // only resets stream UI flags — message/reference state is owned by useConversation
  useEffect(() => {
    reset();
  }, [id, reset]);
  const { mutate } = useMutation({
    mutationFn: async ({
      data,
      files,
    }: {
      data: ChatFormData;
      files?: File[];
    }) => {
      if (id) {
        return streamMessage({ data, conversation_id: id });
      } else {
        setMessages([]);
        const newConversation = (await conversationsApi.createConversation({
          ...data,
          name: data.message,
        })) as INewConversation;
        navigation(`${newConversation?.id}`);

        if (files && files.length > 0) {
          await uploadFilesToConversation(newConversation.id, files);
        }

        queryClient.invalidateQueries({ queryKey: ["conversations"] });
        return streamMessage({ data, conversation_id: newConversation?.id });
      }
    },
    onMutate: () => {
      scrollToBottom({ animation: "smooth" });
    },
  });
  const form = useForm<ChatFormData>({
    defaultValues: {
      message: "",
      llm_config_id: llm_config_id,
      planner_llm_config: planner_llm_config,
      mode: mode,
      temperature: temperature,
      max_tokens: max_tokens,
      metadata: {
        tools: tools,
        language: language,
        flow: null,
        collection: null,
        model: null,
      },
      name: t("playground.newConversation"),
    },
  });
  const navigation = useNavigate();
  const { data, isLoading } = useConversation(id);

  useEffect(() => {
    form.setValue("metadata.tools", tools);
  }, [tools]);

  useEffect(() => {
    form.setValue("metadata.language", language);
  }, [form, language]);

  useEffect(() => {
    const defaultLanguage =
      normalizeOptionalLanguage(i18n.resolvedLanguage || i18n.language) ||
      "en";

    form.setValue("metadata.language", defaultLanguage);
    setLanguage(defaultLanguage);
  }, [form, i18n.language, i18n.resolvedLanguage, setLanguage]);

  const handleFileUpload = async (files: File[]) => {
    if (id) {
      await uploadFilesToConversation(id, files);
      queryClient.invalidateQueries({ queryKey: ["documents", id] });
    } else {
      const newConversation = (await conversationsApi.createConversation({
        name: t("playground.newConversation"),
        metadata: {
          tools: tools,
          language: language,
          flow: null,
          collection: null,
          model: null,
        },
        llm_config_id: llm_config_id,
        temperature: temperature,
        max_tokens: max_tokens,
      })) as INewConversation;

      navigation(`${newConversation?.id}`);

      if (files && files.length > 0) {
        await uploadFilesToConversation(newConversation.id, files);
      }

      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({
        queryKey: ["documents", newConversation.id],
      });
    }
  };
  return (
    <div
      className={cn(
        "flex-1 min-w-0 rounded-[12px] md:rounded-[16px] relative overflow-hidden flex flex-col items-center justify-center pt-[30px] px-2 sm:px-4",
        {
          "items-start": !!id,
        },
      )}
      style={{
        backgroundColor: storedTheme.card.backgroundColor[theme.palette.mode],
      }}
    >
      <PlaygroundNavbar
        title={data?.name ?? ""}
        id={id ?? ""}
        actions={
          <>
            {/* Legacy Playground navigation is intentionally hidden from users.
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="hover:bg-accent bg-[var(--playground-icon-bg)] data-[state=open]:bg-accent h-8 w-8 p-0 rounded-full"
                >
                  <MoreHorizontal className="h-4 w-4 text-foreground" />
                  <span className="sr-only">{t("playground.openMenu")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    localStorage.setItem("playground-preference", "legacy");
                    if (location.pathname.startsWith("/playground")) {
                      navigate("/legacyplayground");
                    } else {
                      navigate("/legacyplayground-standalone");
                    }
                  }}
                >
                  {t("playground.navigateToOlderPlayground")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            */}
          </>
        }
      />
      {!!!id && (
        <div
          style={{
            background:
              "radial-gradient(50% 50% at 50% 50%, var(--primary) 0%, transparent 100%)",
          }}
          className="hidden md:block absolute w-[1540px] h-[673px] -left-[242px] rtl:left-auto rtl:right-[-242px] -bottom-[386px] rounded-[1540px] opacity-20 overflow-hidden pointer-events-none"
        >
          {isInferenceOnly && (
            <PlaygroundUsageToast
              organizationId={currentOrganization?.id ?? null}
            />
          )}
        </div>
      )}
      {isLoading && (
        <div className="h-full w-full flex items-center justify-center">
          <Spinner className="w-11 h-11" />
        </div>
      )}
      {data && (
        <RenderConversation
          isPlanning={isPlanning}
          isThinking={isThinking}
          agentSteps={agentSteps as any}
          streamActions={streamActions}
          mode={mode}
          messages={messages}
          {...stick}
        />
      )}
      <ToolsContext.Provider value={{ tools, setTools }}>
        <div className=" w-full max-w-[688px] flex flex-col gap-2 mx-auto">
          <ChatInput
            formData={form}
            onSubmit={(data, files) => mutate({ data, files })}
            isLoading={id ? isStreaming(id) : false}
            conversationId={id}
            stop={() => stop(id)}
            onFileUpload={handleFileUpload}
          />
          {/* The Legacy Playground promo is intentionally hidden from users.
          {!!!id && newPlaygroundInfo && (
            <div className="relative bg-[var(--playground-info-bg)] p-3 pe-12 rounded-[8px] flex flex-col min-[520px]:flex-row gap-3 min-[520px]:gap-2 transition-all items-start">
              <div className="shrink-0 w-full min-[520px]:w-auto [&_svg]:h-20 [&_svg]:w-full min-[520px]:[&_svg]:h-[121px] min-[520px]:[&_svg]:w-[121px]">
                <NewPlaygroundLogo />
              </div>

              <div className="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
                <div className="min-w-0">
                  <h2 className="font-semibold text-foreground text-[14px] sm:text-[16px] leading-tight">
                    {t("playground.newPlaygroundTitle")}
                  </h2>
                  <p className="text-[var(--playground-info-text)] text-[12px] sm:text-[14px] font-medium leading-relaxed break-words">
                    {t("playground.newPlaygroundDescription")}
                  </p>
                </div>
                <Button
                  className="bg-none px-0 min-[520px]:px-2 sm:px-3 text-[12px] sm:text-sm h-auto min-h-8 text-start whitespace-normal"
                  variant="ghost"
                  onClick={() => {
                    localStorage.setItem("playground-preference", "legacy");
                    if (location.pathname === "/playground") {
                      navigate("/legacyplayground");
                    } else {
                      navigate("/legacyplayground-standalone");
                    }
                  }}
                >
                  {t("playground.oldPlayground")}
                </Button>
              </div>
              <div className="absolute top-3 end-3 shrink-0">
                <X
                  className="bg-[var(--playground-icon-bg)] rounded-full p-[4px] cursor-pointer"
                  onClick={() => {
                    localStorage.setItem("playground-info-dismissed", "true");
                    setNewPlaygroundInfo(false);
                  }}
                />
              </div>
            </div>
          )}
          */}
        </div>
      </ToolsContext.Provider>
    </div>
  );
};
